import { Button, Icons, Loading, StateNote } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useMyWork, useSession } from '@/lib/api/hooks'
import { personName } from '@/lib/people'
import { PageFrame, Section } from '@/shell/PageFrame'
import { AvailabilityCard } from './Availability'
import { LiveWork, WorkLedger } from './MyWork'
import { PlayerOverview } from './Overview'
import { AnnouncementBanner } from '@/features/announcements/AnnouncementsPage'

/**
 * The player's side of the studio, in the console's own shell: the same
 * column, the same strip and bell, the same page frame, the same settings —
 * with the player's three destinations in place of the studio's. Every
 * answer arrives redacted from the server.
 *
 *   Home           the clock, the month's overview, and the active tasks
 *   Work           every assignment as a compact row
 *   Payments       their monthly statements (features/payments)
 */

type WorkQuery = ReturnType<typeof useMyWork>

/** Home: the clock at the top right, the month at a glance, then the active tasks. */
export function PlayerHomePage() {
  const me = useSession().data?.account ?? null
  const work = useMyWork()
  return (
    <PageFrame
      title="Home"
      description={me ? `Welcome back, ${personName(me)}.` : undefined}
      actions={<AvailabilityCard />}
    >
      <AnnouncementBanner />
      {work.data ? <PlayerOverview rows={work.data.rows} /> : null}
      <Section title="Active Tasks">
        {work.isLoading ? (
          <Loading label="Loading your tasks" />
        ) : work.error || !work.data ? (
          <Unloaded what="Your tasks" work={work} />
        ) : (
          <LiveWork rows={work.data.rows} availability={me?.availability ?? 'inactive'} />
        )}
      </Section>
    </PageFrame>
  )
}

/** Work: every assignment as a compact row — active first, then by month. */
export function PlayerWorkPage() {
  const me = useSession().data?.account ?? null
  const work = useMyWork()
  return (
    <PageFrame title="Work" description="All your assignments, active and completed.">
      {work.isLoading ? (
        <Loading label="Loading your work" />
      ) : work.error || !work.data ? (
        <Unloaded what="Your work" work={work} />
      ) : (
        <WorkLedger rows={work.data.rows} availability={me?.availability ?? 'inactive'} />
      )}
    </PageFrame>
  )
}

function Unloaded({ what, work }: { what: string; work: WorkQuery }) {
  return (
    <StateNote
      title={`${what} could not be loaded.`}
      detail={describeError(work.error)}
      action={
        <Button size="sm" icon={<Icons.retry />} onClick={() => void work.refetch()}>
          Try Again
        </Button>
      }
    />
  )
}
